import { Page } from 'context/PageContext';
import { getApiEndpoint } from 'services';
import { store } from 'store/store';
import { apiInstance } from './Api';

export const submitOpportunityLead = async (pageContext: Partial<Page>) => {
	try {
		// Use the shared apiInstance so the reCAPTCHA request interceptor attaches
		// the `X-Recaptcha-Token` header on open-proxy calls. A bare axios.create()
		// would skip the interceptor and the backend rejects with 400
		// "Missing ReCaptcha Header".
		const axiosInstance = apiInstance(30000);

		const productDetails = getProductDetails(pageContext.metaArea);

		await sendEmailToKakao(productDetails, axiosInstance, pageContext.metaArea);

		// const url = metaArea.items[0].content.quoteOpportunityApiEndpoint.url;
		const url = getApiEndpoint(pageContext.apiEndpoints, 'CalculatorQuoteOpportunity') || '';

		return await axiosInstance.post(url, getBody(productDetails, pageContext.metaArea));
	} catch (e) {
		console.error('Error in submitOpportunityLead:', e);
		throw e;
	}
};

const sendEmailToKakao = async (productDetails, axiosInstance, metaArea: MetaArea | undefined) => {
	try {
		const postboxId = 'codan-calculators-send-oss-not-logged-in';
		const customerReceiptPostboxId = 'codan-calculators-online-salg-kunde-kvittering';

		const postbox = metaArea.items[0].content.calculatorEmailConfiguration?.items?.filter((item) => {
			if (item.content?.postboxNames) {
				for (const postboxNames of item.content.postboxNames) {
					if (postboxNames.postboxName.toLowerCase() === postboxId) {
						return true;
					}
				}
			}
		});

		if (postbox?.length > 0) {
			return await axiosInstance.post(postbox[0].content.url.url, {
				description: productDetails.descriptionEmail,
				subject: 'Codan Kunde ønsker ' + metaArea.items[0].content.productName,
				email: store.getState().calculatorForm['CUSTOMER_EMAIL']?.data?.value,
				postboxId,
				onlineSalesCustomerRecieptPostboxId: customerReceiptPostboxId,
			});
		}
	} catch (e) {
		console.error(e);
	}
};

const getBody = (productDetails, metaArea: MetaArea) => {
	try {
		const consent = getConsent();
		return {
			customer_details: getCustomerDetails(metaArea),
			opportunity_details: {
				description: productDetails.description,
				json_description: JSON.stringify(productDetails.jsonDescription),
				lead_source: productDetails.leadSource,
				sub_lead_source: productDetails.subLeadSource,
			},
			quotes: [], // WAIT for SalesForce to support quotes in the API
			consents: consent ? [consent] : [],
		};
	} catch (e) {
		console.error(e);
		return {};
	}
};

const getConsent = () => {
	const form = store.getState().calculatorForm;

	if (form['CUSTOMER_HAS_FORMER_COMPANY']?.data?.value === 'Y' && form['CUSTOMER_CONSENT']?.data?.value === 'Y') {
		const companyId = form['CUSTOMER_FORMER_COMPANY']?.data?.value;
		const consent = {
			key: 'onlinetermination_private',
			start_date: new Date().toISOString(),
			competitors: [companyId],
		};

		return consent;
	}
};

const getCustomerDetails = (metaArea: MetaArea) => {
	const form = store.getState().calculatorForm;

	const fullName = form['CUSTOMER_FULL_NAME']?.data?.value;

	const lastName = fullName?.split(/[, ]+/)?.pop();

	const firstNames = fullName?.replace(lastName, '')?.trim();

	const address = form['CUSTOMER_FULL_ADDRESS']?.data?.value
		? JSON.parse(form['CUSTOMER_FULL_ADDRESS']?.data?.value)
		: {};

	const customer_details = {
		validated_user: !!form['CUSTOMER_ID']?.data?.value,
		first_name: firstNames?.length > 0 ? firstNames : 'NA',
		last_name: lastName?.length > 0 ? lastName : 'NA',
		social_security_number: form['CUSTOMER_CPR']?.data?.value,
		customer_number: form['CUSTOMER_ID']?.data?.value,
		address: {
			street_name: address?.vejnavn,
			street_number: address?.husnr,
			street_code: address?.vejkode,
			postal_code: address?.postnr,
			city: address?.postnrnavn,
			country: 'DK',
			kvhx: address?.kvhx,
		},
		email: form['CUSTOMER_EMAIL']?.data?.value,
		mobile: form['CUSTOMER_PHONE']?.data?.value,
		//business_brand: '080796cf-3289-45a2-a1b2-5447e45fa099', // AB
		business_brand: metaArea.items[0].content.businessBrand ?? '339996a3-4df2-4230-bfe9-3051a98500cb', // Codan
	};
	return customer_details;
	// Dataforsyningen Address Response after adding kvh/kvhx
	// {
	//     "id": "0a3f509e-49e5-32b8-e044-0003ba298018",
	//     "status": 1,
	//     "darstatus": 3,
	//     "vejkode": "2136",
	//     "vejnavn": "Gasværksvej",
	//     "adresseringsvejnavn": "Gasværksvej",
	//     "husnr": "29",
	//     "etage": "2",
	//     "dør": "th",
	//     "supplerendebynavn": null,
	//     "postnr": "1656",
	//     "postnrnavn": "København V",
	//     "stormodtagerpostnr": null,
	//     "stormodtagerpostnrnavn": null,
	//     "kommunekode": "0101",
	//     "adgangsadresseid": "0a3f507a-7851-32b8-e044-0003ba298018",
	//     "x": 12.55810987,
	//     "y": 55.67016213,
	//     "href": "https://api.dataforsyningen.dk/adresser/0a3f509e-49e5-32b8-e044-0003ba298018"
	//     "kvhx": 123__243_45
	// }
};

export const getProductDetails = (metaArea: MetaArea) => {
	const productDefinition = metaArea.items[0].content;
	const prePackageName = productDefinition.calculatorParameterPrefix;
	const productName = productDefinition.productName;
	const productNameShort = productDefinition.shortProductName;

	const form = store.getState().calculatorForm;

	const prePackageData = {};
	const calculationData = {};
	const personInfoData = {};
	const coverageData = {};

	const submitMappings = store.getState().placeholderDefinitions.submitMappings;

	for (const key in form) {
		if (!form[key]) {
			// can be undefined or null (SUBMIT_INSURANCE_ERROR)
			continue;
		}
		const field = mapField(form[key].data);

		if (!field) {
			console.warn('Field mapping returned undefined for key:', key);
			continue;
		}

		if (key.startsWith('CUSTOMER_')) {
			personInfoData[field.label ?? key] = field.value;
			// Handle special mappings
			specificFieldMapping(key, form, personInfoData);
			handleSubmitMapping(key, personInfoData, field, submitMappings);
		} else if (key.startsWith('COVERAGE_')) {
			if (field.value == 'true') {
				// only included coverages
				coverageData[field.label ?? key] = field.value;
				handleSubmitMapping(key, coverageData, field, submitMappings);
			}
		} else if (key.startsWith(prePackageName)) {
			prePackageData[field.label ?? key] = field.value;
			handleSubmitMapping(key, prePackageData, field, submitMappings);
		} else {
			// this includes SELECTED_PACKAGE and other fields that are not categorized, we want to capture them all in calculationData
			calculationData[field.label ?? key] = field.value;
			handleSubmitMapping(key, calculationData, field, submitMappings);
		}
	}

	const allData = { ...personInfoData, ...prePackageData, ...calculationData, ...coverageData };

	const jsonDescription = {
		products: [
			{
				productName,
				status: 'OK',
				description: {
					personInfoData,
					prePackageData,
					calculationData,
					coverageData,
				},
			},
		],
	};
	const yearly = form['SELECTED_PACKAGE_PAYMENT_FREQUENCY']?.data?.value === '1';
	const price = yearly
		? form['SELECTED_PACKAGE_YEARLY_PRICE']?.data?.value
		: form['SELECTED_PACKAGE_MONTHLY_PRICE']?.data?.value;
	const newLine = '\n';

	let description = price + ' ' + productNameShort + newLine;
	let descriptionEmail = '<h2>' + price + ' ' + productNameShort + '</h2><br/>';

	for (const key in allData) {
		description += key + ': ' + allData[key] + newLine;
		descriptionEmail += '<b>' + key + '</b>: ' + allData[key] + '<br/>';
	}
	return {
		description,
		descriptionEmail,
		jsonDescription,
		leadSource: metaArea.items[0].content.leadSource ?? 'Codan',
		subLeadSource: metaArea.items[0].content.subLeadSource ?? 'codan.dk - Online beregner',
	};
};

const handleSubmitMapping = (key: string, data, field, submitMappings) => {
	// if field has no label and exists in submitMappings, use the mapping key as label
	if (submitMappings[key]) {
		if (submitMappings[key] !== 'REMOVE_FROM_SUBMIT') {
			data[submitMappings[key]] = field.value;
		}
		delete data[key]; // remove the original key without label
	}
};

const specificFieldMapping = (key: string, form, data) => {
	if (
		key === 'CUSTOMER_FORMER_COMPANY' &&
		form['CUSTOMER_HAS_FORMER_COMPANY']?.data?.value === 'Y' &&
		form['CUSTOMER_CONSENT']?.data?.value === 'Y'
	) {
		const companyId = form['CUSTOMER_FORMER_COMPANY']?.data?.value;
		data['Nuværende forsikringsselskab id'] = companyId ?? 'NA';
	}
};

const mapField = (formField) => {
	switch (formField.type) {
		case 'SelectFieldBlock':
			// Key by UID (formField.id), not stepTitle, so multiple Selects sharing a step
			// (e.g. CalculationOptions "Pakkerne" mileage + excess) don't collide on the same
			// description key. The readable option text stays as the value.
			return {
				label: formField.id,
				value: formField.label,
			};

		case 'RadioCardFieldBlock':
		case 'RadioSliderBlock':
		case 'DatePickerFieldBlock':
			return {
				label: formField.stepTitle,
				value: formField.label,
			};

		case 'PACKAGE':
		case 'COVERAGE':
		case 'UserConsentBlock':
		case 'InputTextfieldBlock':
			return {
				label: formField.inputLabel ?? formField.label,
				value: formField.value,
			};
		case 'SearchFieldBlock':
			return {
				label: formField.inputLabel ?? formField.label,
				value: formField.label,
			};
		default:
			if (formField?.value || formField?.label || formField?.id) {
				return {
					label: formField.label || formField.id || 'N/A',
					value: formField.value || 'N/A',
				};
			} else {
				console.error("Field doesn't have any value", formField);
				return undefined;
			}
	}
};
